import { inject } from '@angular/core';
import { CanActivateFn, Router } from '@angular/router';
import { AuthService } from '../services/auth.service';
import { LockStateService } from '../services/lock-state.service';

/**
 * B052: while this tab is locked, pages outside the app shell (no lock overlay) must not open.
 *
 * Used on /change-password and /tele/:roomCode. Typing either address while locked used to open a page with no lock
 * on it (and Change Password allowed unlimited guesses at the current password). Patients who are not signed in are
 * unaffected: the guard always passes when the tab is not locked.
 */
export const lockedRouteGuard: CanActivateFn = (route) => {
  const lock = inject(LockStateService);
  const auth = inject(AuthService);
  const router = inject(Router);

  if (!lock.isLocked) return true;

  // The forced flow (TC-0016 / TC-0099 / TC-0100, an admin reset while locked): the server will not serve anything
  // else until the password is changed, so this page must stay reachable.
  if (route.routeConfig?.path === 'change-password' && auth.currentUser?.mustChangePassword === true) return true;

  // An in-app link or Back: cancel the navigation; the locked screen stays where it is.
  if (router.navigated) return false;

  // Start-up renewal still in flight (briefly not signed in) — keep the flag and go to sign-in.
  if (!auth.isLoggedIn) return router.parseUrl('/login');

  // A typed address or a reload: open the role's home page inside the layout, which shows the lock
  // (the same pattern AuthGuard uses for a wrong role).
  auth.redirectAfterLogin();
  return false;
};
